// Recruitment agency software guide (/guides/recruitment-agency-software).
// Evidence policy: every vendor fact comes from that vendor's own website, read on
// AGENCY_SOFTWARE_REVIEWED and listed in `sources`. "Not stated" means the vendor's pages
// did not say, not that the feature is missing. Beatview is not an ATS and is not ranked here.

import type { VendorReview } from '@/components/page/VendorReviews';

export const AGENCY_SOFTWARE_REVIEWED = '2026-09-30';
export const AGENCY_SOFTWARE_REVIEWED_LABEL = '30 September 2026';

export type AgencyPlatform = VendorReview & {
  builtFor: string;
  deskTypes: string;
  aiFeatures: string;
  pricingShort: string;
  freeTrial: string;
  sources: { label: string; url: string }[];
};

export const AGENCY_PLATFORMS: AgencyPlatform[] = [
  {
    id: 'bullhorn',
    pricingHref: '/guides/bullhorn-pricing',
    name: 'Bullhorn',
    website: 'https://www.bullhorn.com/',
    category: 'Staffing ATS and CRM with AI agents',
    bestFor: 'Staffing and recruitment agencies of any size that want ATS, CRM, AI agents and the largest partner marketplace in one platform',
    summary: 'Bullhorn combines applicant tracking and CRM for staffing firms with its Amplify AI layer. Amplify Digital Workers handle sourcing, screening and outreach, including structured interviews by chat or voice, and Amplify Chat is a staffing language model inside Bullhorn. In North America, Bullhorn also covers timesheets and pay and bill.',
    strengths: [
      'Amplify Digital Workers source, screen by chat or voice, and run outreach',
      'Search and Match ranks candidates with AI relevancy scoring',
      'Bullhorn Marketplace with 300+ pre-integrated partners',
      'Published entry prices: Starter $99 and Core $165 per user a month (UK £80 and £120)',
      'Executive search support, with Invenias by Bullhorn for boutique firms',
    ],
    watchOuts: [
      'Pro and Max, which carry most AI features, are priced on request',
      'Agentic screening is listed in the Max edition',
      'No native video interviewing is named; video runs through partners such as Zoom',
      'Middle and back office is offered in North America only',
      'Contract length and billing frequency are not published',
    ],
    pricing: 'Starter $99 and Core $165 per user/month (UK £80 and £120); Pro and Max on request',
    details: [['Built for', 'Staffing and recruitment agencies, small to enterprise'], ['Marketplace', 'Bullhorn Marketplace, 300+ partners']],
    builtFor: 'Staffing and recruitment agencies of every size',
    deskTypes: 'Staffing jobs and shifts; executive search',
    aiFeatures: 'Amplify: Digital Workers (sourcing, chat or voice screening, outreach), Chat, Search and Match',
    pricingShort: 'From $99/user/month (Starter); Pro and Max on request',
    freeTrial: 'Not stated',
    sources: [
      { label: 'Bullhorn home page', url: 'https://www.bullhorn.com/' },
      { label: 'Bullhorn UK home page', url: 'https://www.bullhorn.com/uk/' },
      { label: 'Bullhorn pricing', url: 'https://www.bullhorn.com/pricing/' },
      { label: 'Bullhorn Amplify', url: 'https://www.bullhorn.com/products/amplify/' },
      { label: 'Amplify Digital Workers', url: 'https://www.bullhorn.com/products/amplify/digital-workers/' },
      { label: 'Bullhorn Marketplace', url: 'https://www.bullhorn.com/marketplace/' },
      { label: 'Bullhorn executive search', url: 'https://www.bullhorn.com/executive-search/' },
    ],
  },
  {
    id: 'jobadder',
    name: 'JobAdder',
    website: 'https://jobadder.com/',
    category: 'Agency ATS and CRM',
    bestFor: 'Growing recruitment agencies with perm, temp or dual desks that want one ATS and CRM with job posting and a large partner marketplace',
    summary: 'JobAdder is a combined ATS and CRM for recruitment agencies, covering business development, job posting, applicant tracking, placements and reporting. Its AI, Adder Intelligence, writes a summary and relevancy rating for every applicant, client-ready candidate overviews and job ads. JobAdder is part of the SEEK Group.',
    strengths: [
      'Purpose-built features for perm and temp or contract work, including dual desks',
      'Adder Intelligence: a summary and relevancy rating for every applicant',
      'AI job ads and client-ready candidate overviews',
      '100+ integration partners and 200+ job boards',
      'States that a human must always make the final shortlisting decision',
    ],
    watchOuts: [
      'Prices are not published; plans are quoted by user band',
      'Recruiter Lite is capped at 5 users',
      'No AI interviews of its own; video interviewing comes from marketplace partners',
      'A free demo is offered; a free trial is not stated',
    ],
    pricing: 'Not published; quoted by user band (Lite 1–5, Essential 6–20, Pro 21+ users)',
    details: [['Regions', 'Offices in Australia, New Zealand, the UK, the US and Canada'], ['Marketplace', 'JobAdder Marketplace, 100+ partners']],
    builtFor: 'Growing recruitment and staffing agencies',
    deskTypes: 'Perm, temp and contract, including dual desks',
    aiFeatures: 'Adder Intelligence: applicant summaries and relevancy ratings, job ads, candidate overviews, matching',
    pricingShort: 'Quote, by user band',
    freeTrial: 'Free demo; trial not stated',
    sources: [
      { label: 'JobAdder home page', url: 'https://jobadder.com/' },
      { label: 'JobAdder ATS and CRM', url: 'https://jobadder.com/jobadder-ats-crm/' },
      { label: 'JobAdder AI recruitment software', url: 'https://jobadder.com/ai-recruitment-software/' },
      { label: 'JobAdder pricing', url: 'https://jobadder.com/pricing/' },
      { label: 'JobAdder integration partners', url: 'https://jobadder.com/platform-feature/integration-partners/' },
    ],
  },
  {
    id: 'vincere',
    name: 'Vincere',
    website: 'https://www.vincere.io/',
    category: 'Agency ATS and CRM with AI and video',
    bestFor: 'UK and APAC agencies, from one-person startups to multi-desk firms, that want CRM, ATS, AI and one-way video interviews on a rolling monthly contract',
    summary: 'Vincere, now Access Vincere Evo from The Access Group, combines CRM, ATS, automation and AI for recruitment agencies. Copilot AI is standard on every plan: it scores candidates on a 1 to 5 star scale and drafts job ads and interview questions. Add-ons cover temp timesheets and one-way video interviews (Vinneo).',
    strengths: [
      'Published prices: Startup £150 a month for one user, Core £95 per user a month',
      '30-day rolling contracts on Startup and Core',
      'Copilot AI on every plan: candidate scoring, job ads and interview questions',
      'Its own one-way video interview tool, Vinneo',
      'Named solutions for perm, contract, temp and executive search',
    ],
    watchOuts: [
      'A one-off onboarding and migration fee applies; the amount is not published',
      'No free trial of the core product; a demo is offered instead',
      'Pro (7+ users) is priced per agency, from £85 per user a month',
      'The size of the partner marketplace is not stated',
    ],
    pricing: 'Startup £150/month (1 user); Core £95/user/month (2–7 users); Pro from £85/user/month (7+); onboarding fee extra',
    details: [['Regions', 'UK and APAC, as part of The Access Group'], ['Video interviews', 'Vinneo one-way video add-on']],
    builtFor: 'Recruitment agencies, single-user to enterprise',
    deskTypes: 'Perm, contract, temp, executive search',
    aiFeatures: 'Access Evo: Copilot scoring (1–5 stars), job ads, meeting transcription, AI agents',
    pricingShort: 'From £95/user/month (Core); Startup £150/month',
    freeTrial: 'No; demo offered',
    sources: [
      { label: 'Access Vincere Evo home page', url: 'https://www.vincere.io/' },
      { label: 'Vincere AI recruitment software', url: 'https://www.vincere.io/ai-recruitment-software/' },
      { label: 'Vincere pricing', url: 'https://www.vincere.io/pricing/' },
      { label: 'Vinneo video interviews', url: 'https://www.vincere.io/vinneo/' },
      { label: 'Vincere integrations', url: 'https://www.vincere.io/integrations/' },
      { label: 'About Vincere', url: 'https://www.vincere.io/about/' },
    ],
  },
  {
    id: 'firefish',
    name: 'Firefish',
    website: 'https://www.firefishsoftware.com/',
    category: 'UK agency recruitment CRM',
    bestFor: 'Independent and mid-market UK agencies, 1 to 200 users, running perm, contract and temp desks',
    summary: 'Firefish is a recruitment CRM built for UK agencies, with separate workflows for perm, temp (shift scheduling, AWR and timesheets) and contract (rates, compliance and renewals). Firefish AI Agents handle skills tagging, candidate insight, matching, emails and content.',
    strengths: [
      'Separate perm, temp and contract workflows in one CRM',
      'Temp tools: shift scheduling, AWR and timesheets',
      'AI Agents for matching, data enrichment, emails and content',
      'Match AI ranks candidates against jobs',
      'Certified partner marketplace and an open API',
    ],
    watchOuts: [
      'Prices are not published, apart from add-on modules from £150 each',
      'The vendor says it is not designed for 500+ user firms or agencies mainly outside the UK',
      'No AI screening or video interviews of its own',
      'Free trial not stated',
    ],
    pricing: 'Not published (quote); Core, Scale and Enterprise plans; add-on modules from £150',
    details: [['Regions', 'UK first; customers in 25+ countries'], ['Marketplace', 'Certified Partners and an open API']],
    builtFor: 'Independent and mid-market UK agencies (1–200 users)',
    deskTypes: 'Perm, contract and temp',
    aiFeatures: 'AI Agents (skills, insight, matching, email, content, enrichment); Match AI',
    pricingShort: 'Quote; add-on modules from £150',
    freeTrial: 'Not stated',
    sources: [
      { label: 'Firefish home page', url: 'https://www.firefishsoftware.com/' },
      { label: 'Firefish job workflows', url: 'https://www.firefishsoftware.com/core-modules/job-workflows' },
      { label: 'Firefish AI Agents', url: 'https://www.firefishsoftware.com/core-modules/ai-agents' },
      { label: 'Firefish pricing', url: 'https://www.firefishsoftware.com/pricing' },
      { label: 'Firefish integrations', url: 'https://www.firefishsoftware.com/product/integrations' },
    ],
  },
  {
    id: 'recruit-crm',
    pricingHref: '/guides/recruit-crm-pricing',
    name: 'Recruit CRM',
    website: 'https://recruitcrm.io',
    category: 'AI-first ATS and CRM for agencies',
    bestFor: 'Small and mid-sized agencies, contract staffing and executive search firms that want AI agents, published per-user prices and a free trial',
    summary: 'Recruit CRM is an ATS and CRM for recruitment agencies with AI agents for parsing, candidate submissions, CV formatting and candidate pitching, plus AI sourcing and matching. Add-ons cover timesheets and contractor pay, multiposting and a website builder, and it publishes an MCP server for Claude and ChatGPT.',
    strengths: [
      'Published prices from $49 per user a month, billed annually',
      'A 30-day free trial',
      'AI agents for submissions, CV formatting and candidate pitching',
      'Call recording with AI call summaries',
      'An MCP server, an open API, and Zapier with 5,000+ apps',
    ],
    watchOuts: [
      'Candidate matching is limited to 2 matches on Pro and 50 on Business',
      'Workflow automation, advanced analytics and data enrichment are on Enterprise',
      'No AI interviewer or video interviews of its own; partners provide them',
      'Temp desks are not named',
    ],
    pricing: 'Pro $49, Business $79, Enterprise $129 per user/month billed annually ($59, $89, $159 monthly)',
    details: [['Regions', 'Agencies in 100+ countries'], ['Integrations', 'Zapier, 51 partners, open API, MCP']],
    builtFor: 'Recruitment agencies, contract staffing and executive search firms',
    deskTypes: 'Permanent (retained and contingent), contract, executive search',
    aiFeatures: 'AI agents (submissions, CV formatting, pitching), AI sourcing and matching, call summaries, MCP',
    pricingShort: 'From $49/user/month (annual)',
    freeTrial: '30 days',
    sources: [
      { label: 'Recruit CRM home page', url: 'https://recruitcrm.io/' },
      { label: 'Recruit CRM AI agents', url: 'https://recruitcrm.io/ai-agents-recruitment/' },
      { label: 'Recruit CRM contract staffing', url: 'https://recruitcrm.io/contract-staffing-software/' },
      { label: 'Recruit CRM pricing', url: 'https://recruitcrm.io/pricing/' },
      { label: 'Recruit CRM integrations', url: 'https://recruitcrm.io/integrations/' },
      { label: 'Recruit CRM MCP', url: 'https://recruitcrm.io/recruit-crm-mcp/' },
    ],
  },
  {
    id: 'loxo',
    pricingHref: '/guides/loxo-pricing',
    name: 'Loxo',
    website: 'https://www.loxo.co',
    category: 'Talent intelligence platform with AI agents',
    bestFor: 'Direct hire, executive search and staffing firms that source heavily and want AI agents to build longlists and ranked shortlists',
    summary: 'Loxo combines an ATS, recruiting CRM, sourcing on its own 850M-person talent graph, multichannel outreach and a sales CRM. Its agents take intake notes, write job descriptions, build longlists of up to 10,000 candidates and rank shortlists with evidence for a recruiter to review.',
    strengths: [
      'Sourcing on an 850M-person talent graph inside the ATS',
      'Longlist and Shortlist agents that rank candidates with evidence',
      'An AI Notetaker for calls on Zoom, Google Meet and Teams',
      'Job board and back-office integrations, including Broadbean, Reed, Totaljobs and Xero',
      'A 7-day free trial of Professional, with no card needed',
    ],
    watchOuts: [
      'Annual plans are paid up front',
      'The full set of agents is on Professional, with 2,500 AI credits per seat a month',
      'Some agents, including scheduling and outreach, are marked coming soon',
      'Contract and temp desks are not named, and LinkedIn and Zapier are not listed',
    ],
    pricing: 'Core from $149 and Professional from $199 per user/month, billed annually; Enterprise custom',
    details: [['Sourcing', '850M-person talent graph'], ['Integrations', 'Loxo Marketplace, MCP, API on Professional']],
    builtFor: 'Direct hire, executive search, staffing and RPO firms',
    deskTypes: 'Direct hire, executive search, staffing, RPO',
    aiFeatures: 'Agents for intake, job descriptions, longlists, shortlists, submittals and CRM updates; AI Notetaker; MCP',
    pricingShort: 'From $149/user/month (annual)',
    freeTrial: '7 days (Professional)',
    sources: [
      { label: 'Loxo home page', url: 'https://www.loxo.co/' },
      { label: 'Loxo staffing agency software', url: 'https://www.loxo.co/solutions/staffing-agency-software' },
      { label: 'Loxo AI agents', url: 'https://www.loxo.co/ai-agents' },
      { label: 'Loxo pricing', url: 'https://www.loxo.co/pricing' },
      { label: 'Loxo Marketplace', url: 'https://www.loxo.co/loxo-marketplace' },
    ],
  },
  {
    id: 'recruiterflow',
    pricingHref: '/guides/recruiterflow-pricing',
    name: 'Recruiterflow',
    website: 'https://recruiterflow.com',
    category: 'AI-native ATS and CRM for search firms',
    bestFor: 'Retained, contingent and executive search firms that want an ATS and CRM with sequences, reporting and AI agents',
    summary: 'Recruiterflow is an ATS and CRM for search and recruiting firms, with multichannel sequences, workflow automation and reporting. Its AI suite, AIRA, handles notes, data updates, matching against plain-English criteria, sourcing from 850M+ profiles and submissions.',
    strengths: [
      'Built for retained, contingent and executive search',
      'AIRA agents for notes, field updates, matching, sourcing and submissions',
      'Multichannel sequences and workflow automation',
      '61 integrations, including UK job boards, phone systems and timesheet tools',
      'Prices shown in USD, EUR or GBP',
    ],
    watchOuts: [
      'The AI agents (AIRA plan) are priced on request',
      'The Platform plan is $119 per user a month billed annually, or $149 monthly',
      'Free trial not stated',
      'No AI interviewer; video interviews come from partners',
    ],
    pricing: 'Platform plan $119/user/month billed annually ($149 monthly); AIRA plan on request',
    details: [['Customers', '2,100+ search firms'], ['Integrations', '61 partners and an open API']],
    builtFor: 'Retained, contingent and executive search firms',
    deskTypes: 'Retained, contingent, executive search',
    aiFeatures: 'AIRA agents: notes, matching, sourcing, submissions, field updates',
    pricingShort: 'From $119/user/month (annual); AI plan on request',
    freeTrial: 'Not stated',
    sources: [
      { label: 'Recruiterflow home page', url: 'https://recruiterflow.com/' },
      { label: 'Recruiterflow ATS', url: 'https://recruiterflow.com/applicant-tracking-software' },
      { label: 'Recruiterflow AI', url: 'https://recruiterflow.com/ai' },
      { label: 'Recruiterflow pricing', url: 'https://recruiterflow.com/pricing' },
      { label: 'Recruiterflow integrations', url: 'https://recruiterflow.com/integrations' },
    ],
  },
  {
    id: 'manatal',
    pricingHref: '/guides/manatal-pricing',
    name: 'Manatal',
    website: 'https://www.manatal.com',
    category: 'AI recruitment software with an AI Interviewer',
    bestFor: 'Budget-conscious agencies and headhunters, especially in Asia, that want a low per-user price, wide job posting and an optional AI video interviewer',
    summary: 'Manatal combines an ATS, a recruitment CRM, job posting to 2,500+ channels, AI candidate enrichment and scoring, and an optional AI Interviewer that runs first-round video screening interviews around the clock. It has solution pages for staffing agencies, recruitment agencies and executive search.',
    strengths: [
      'The lowest published price in this guide: from $15 per user a month billed yearly',
      'Job posting to 2,500+ channels, including SEEK, JobStreet and CV-Library',
      'AI scoring, recommendations and candidate enrichment on every plan',
      'An AI Interviewer add-on for first-round video screening',
      'A 14-day free trial, with no card needed',
    ],
    watchOuts: [
      'The AI Interviewer add-on price is not published',
      'MCP, Zapier and the open API are on Enterprise Plus ($55 per user a month yearly)',
      'Perm, contract and temp desk types are not named',
      'Offices are in Singapore and Bangkok',
    ],
    pricing: 'Professional $15, Enterprise $35, Enterprise Plus $55 per user/month billed yearly ($19, $39, $59 monthly)',
    details: [['Regions', '135+ countries; offices in Singapore and Bangkok'], ['Interviews', 'AI Interviewer add-on (video)']],
    builtFor: 'HR teams, recruitment agencies and headhunters',
    deskTypes: 'Staffing, recruitment agencies, executive search',
    aiFeatures: 'AI scoring, recommendations, enrichment, Copilot; AI Interviewer add-on; MCP on Enterprise Plus',
    pricingShort: 'From $15/user/month (yearly)',
    freeTrial: '14 days',
    sources: [
      { label: 'Manatal home page', url: 'https://www.manatal.com/' },
      { label: 'Manatal for staffing agencies', url: 'https://www.manatal.com/staffing-agencies' },
      { label: 'Manatal AI Interviewer', url: 'https://www.manatal.com/features/ai-interviewer' },
      { label: 'Manatal pricing', url: 'https://www.manatal.com/pricing' },
      { label: 'Manatal integrations', url: 'https://www.manatal.com/integrations' },
    ],
  },
  {
    id: 'zoho-recruit',
    pricingHref: '/guides/zoho-recruit-pricing',
    name: 'Zoho Recruit',
    website: 'https://www.zoho.com/recruit/',
    category: 'Staffing ATS and CRM (Staffing Agency Edition)',
    bestFor: 'Cost-conscious staffing agencies, especially temp and contract, that want client and vendor portals and a free plan to start',
    summary: 'Zoho Recruit’s Staffing Agency Edition adds client handling, client and vendor portals, formatted resumes and multi-org support to its ATS and CRM. Zia, Zoho’s own AI, matches candidates, writes job descriptions and summaries, and runs sourcing and screening chatbots. One-way or live video interviews are an add-on.',
    strengths: [
      'A Forever Free plan, and a 15-day free trial of paid plans',
      'Paid plans from $25 per user a month billed annually (US prices)',
      'Client and vendor portals built for staffing',
      'Zia AI: matching, summaries and screening chatbots',
      'A marketplace of 200+ apps and 75+ job boards',
    ],
    watchOuts: [
      'Prices vary by the visitor’s country',
      'Which staffing plan includes Zia AI is not stated',
      'Video interviews cost extra: $12 per job slot (US)',
      'Perm and executive search are not named on the staffing page',
    ],
    pricing: 'Forever Free; Standard $25, Professional $50, Enterprise $75 per user/month billed annually (US prices)',
    details: [['Video interviews', 'One-way or live, $12 per job slot'], ['Integrations', '200+ apps, 75+ job boards']],
    builtFor: 'Staffing agencies (Staffing Agency Edition)',
    deskTypes: 'Temp and contract staffing',
    aiFeatures: 'Zia: matching, job descriptions, summaries, sourcing and screening chatbots',
    pricingShort: 'Free plan; from $25/user/month (annual, US)',
    freeTrial: '15 days, plus a free plan',
    sources: [
      { label: 'Zoho Recruit home page', url: 'https://www.zoho.com/recruit/' },
      { label: 'Zoho Recruit for staffing agencies', url: 'https://www.zoho.com/recruit/staffing-agency-software.html' },
      { label: 'Zoho Recruit AI recruitment', url: 'https://www.zoho.com/recruit/ai-recruitment.html' },
      { label: 'Zoho Recruit plan comparison', url: 'https://www.zoho.com/recruit/plan-comparison.html' },
      { label: 'Zoho Recruit integrations', url: 'https://www.zoho.com/recruit/integrations.html' },
    ],
  },
  {
    id: 'crelate',
    name: 'Crelate',
    website: 'https://www.crelate.com',
    category: 'Recruiting CRM and ATS for staffing and search',
    bestFor: 'US direct hire, contract and executive search firms that want AI sourcing, onboarding and back-office connections in one platform',
    summary: 'Crelate brings sourcing, a recruiting CRM and applicant tracking together with AI agents, including a Discovery Agent that searches about 1 billion profiles and a Co-Pilot that summarises resumes and drafts interview questions. Crelate Hire handles onboarding and compliance, and it connects to back-office, payroll and VMS systems.',
    strengths: [
      'Discovery Agent sourcing across about 1 billion profiles',
      'Co-Pilot resume summaries and interview question drafts',
      'Onboarding and US compliance forms (I-9, E-Verify) with Crelate Hire',
      'Back-office, payroll and VMS integrations, with 80+ partners',
      'A free trial with no card needed',
    ],
    watchOuts: [
      'Essentials is billed annually; Business Plus and Platform are priced on request',
      'Crelate Hire is priced per hire',
      'Its MCP server is marked coming soon',
      'No AI interviewer; one-way video comes from partners',
    ],
    pricing: 'Essentials $85/user/month (annual); Business $119/user/month; Business Plus and Platform on request',
    details: [['Regions', 'US-based, with US onboarding forms'], ['Integrations', '80+ partners, VMS Connect, open API']],
    builtFor: 'Staffing and search firms: direct hire, executive search, contract',
    deskTypes: 'Direct hire, contract, executive search, high-volume staffing',
    aiFeatures: 'Discovery Agent sourcing, Co-Pilot (resume summaries, interview questions), data quality agents',
    pricingShort: 'From $85/user/month (annual)',
    freeTrial: 'Yes; length not stated',
    sources: [
      { label: 'Crelate home page', url: 'https://www.crelate.com/' },
      { label: 'Crelate for recruiting firms', url: 'https://www.crelate.com/customers/recruiting-agency-software' },
      { label: 'Crelate AI', url: 'https://www.crelate.com/ai' },
      { label: 'Crelate pricing', url: 'https://www.crelate.com/pricing' },
      { label: 'Crelate back office', url: 'https://www.crelate.com/back-office' },
      { label: 'Crelate marketplace', url: 'https://www.crelate.com/marketplace' },
    ],
  },
];

export const STACK_LAYERS: { title: string; body: string; examples: string; href?: string; link?: string; core?: boolean }[] = [
  { title: 'ATS and CRM', core: true, body: 'The system of record: candidates, clients, jobs, submissions and placements in one database. Every other tool reads from it or writes to it.', examples: AGENCY_PLATFORMS.map(p => p.name).join(' · '), href: '#comparison', link: 'Compare them' },
  { title: 'Screening and first interviews', body: 'Scores every inbound application against the client’s criteria, runs structured first interviews and packages the evidence for the client.', examples: 'Beatview, and the AI interview tools in our AI recruiting software guide', href: '/guides/best-ai-recruiting-software', link: 'Compare AI tools' },
  { title: 'Sourcing and outreach', body: 'Finds passive candidates beyond your own database and runs outreach sequences. Some agency platforms include it; others add a specialist tool.', examples: 'Built into Bullhorn, Loxo, Recruiterflow and Crelate; specialists such as hireEZ and Juicebox', href: '/guides/best-ai-recruiting-software', link: 'Compare sourcing tools' },
  { title: 'Temp and contract back office', body: 'Shifts, timesheets, pay and bill, and compliance for contractors. Perm-only agencies can skip this layer.', examples: 'Bullhorn (North America), Firefish temp workflows, Vincere and Recruit CRM timesheet add-ons, Crelate back-office connections' },
  { title: 'AI assistants across the stack', body: 'Claude, ChatGPT and Gemini can work across your ATS and your screening tool through MCP servers, where both publish one.', examples: 'MCP servers from Recruit CRM, Loxo, Manatal, Zoho Recruit and Beatview', href: '/guides/recruiting-mcp-servers', link: 'Compare MCP servers' },
];

export const BUYER_QUESTIONS: string[] = [
  'Who migrates our data from the current system, and what does it cost?',
  'Is pricing per user, and what does one more consultant add?',
  'What is the minimum contract term and the notice period?',
  'Which job boards can we post to, and is multiposting included?',
  'Where is candidate data hosted, and how do you handle UK GDPR requests?',
  'Which AI features are included, and what data do they send to outside AI models?',
  'Which integrations are native, and is there an open API?',
  'How do temp and contract desks handle timesheets and invoicing?',
  'Can clients see submitted candidates without a login?',
];

export const AGENCY_SHORT_ANSWER = 'Recruitment agencies run one system of record, an ATS and CRM built for agency work, and add specialist tools around it. Bullhorn, which says it powers 10,000+ staffing agencies, has the largest partner marketplace here and puts AI agents in its higher editions. JobAdder, Vincere and Firefish are built around agencies in the UK and Asia-Pacific. Manatal, Zoho Recruit and Recruit CRM publish the lowest per-user prices, and Loxo, Recruiterflow and Crelate lean toward search and sourcing. Whichever you choose, Beatview adds AI screening and structured video interviews beside it.';

export const FIT_ROWS: [string, string][] = [
  ['Runs a large staffing business with temp shifts, and pay and bill in North America', 'Bullhorn'],
  ['Is a UK agency with perm, contract and temp desks', 'Firefish, Vincere, Bullhorn'],
  ['Operates across Australia, New Zealand and the UK', 'JobAdder, Vincere'],
  ['Wants a rolling monthly contract and a published price', 'Vincere (Startup and Core), Recruit CRM, Manatal'],
  ['Is small or new and watches every seat', 'Manatal, Zoho Recruit (free plan), Recruit CRM'],
  ['Runs retained or executive search', 'Recruiterflow, Loxo, Bullhorn, Vincere'],
  ['Sources heavily and wants AI to build longlists', 'Loxo, Crelate, Recruiterflow'],
  ['Onboards US contractors with I-9 and E-Verify', 'Crelate'],
  ['Wants AI video interviews without changing its ATS', 'Beatview beside any of them; built-in options are Vincere’s Vinneo, Manatal’s AI Interviewer add-on and Zoho Recruit’s video add-on'],
];

export const AGENCY_SOFTWARE_FAQS = [
  { question: 'What software do recruitment agencies use?', answer: 'Most agencies run one ATS and CRM built for agency work, such as Bullhorn, JobAdder, Vincere, Recruit CRM or Loxo, to hold candidates, clients, jobs and placements. Around it they add job-board multiposting, sourcing tools, AI screening and interviews, and on temp desks, back-office software for timesheets and invoicing.' },
  { question: 'What is the best recruitment software for a small agency?', answer: `Look for a published per-user price, a free trial and no long contract. As of ${AGENCY_SOFTWARE_REVIEWED_LABEL}, Manatal starts at $15 per user a month billed yearly, Zoho Recruit has a free plan and paid plans from $25 (US prices), Recruit CRM starts at $49 with a 30-day free trial, and Vincere’s Startup plan is £150 a month for one user on a 30-day rolling contract. Bullhorn Starter is $99 per user a month.` },
  { question: 'How much does recruitment agency software cost?', answer: `Published prices as of ${AGENCY_SOFTWARE_REVIEWED_LABEL} run from $15 to about $199 per user a month, mostly billed annually: Manatal from $15, Zoho Recruit from $25, Recruit CRM from $49, Crelate from $85, Bullhorn from $99, Recruiterflow from $119 and Loxo from $149. Vincere’s Core plan is £95 per user a month plus an onboarding fee. JobAdder and Firefish quote on request, and most AI features sit in higher tiers.` },
  { question: 'What is the difference between an ATS and a recruitment CRM?', answer: 'An ATS tracks candidates through jobs: applications, stages, interviews and offers. A recruitment CRM manages relationships: clients, contacts, business development and candidate pools over time. Agency platforms combine both, because an agency sells to clients as well as placing candidates.' },
  { question: 'Do I need AI screening software if my ATS already has AI?', answer: 'Often yes, if screening volume is your bottleneck. Agency ATS AI mostly helps with search, matching, writing and summaries. A screening tool such as Beatview scores every inbound application against a client’s criteria, interviews the shortlist by structured video and shows the evidence behind each score, then passes the result to your ATS.' },
  { question: 'Which recruitment agency software works with ChatGPT or Claude?', answer: 'Recruit CRM, Loxo, Manatal and Zoho Recruit publish MCP servers that AI assistants such as Claude and ChatGPT can connect to; Manatal’s is on Enterprise Plus, and Crelate lists one as coming soon. Beatview’s MCP server, included on every plan, lets the same assistant work with candidates who are already screened and interviewed.' },
  { question: 'Is staffing agency software different from recruitment agency software?', answer: 'The terms overlap. “Staffing” is more common in the US and for temp and contract work, and “recruitment” in the UK and for permanent roles. Staffing software usually adds shifts, timesheets, pay and bill, and compliance for contractors.' },
] as const;
